import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi, useDirectory } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { KV, Loading, StatusBadge } from "@/components/common/ui-bits";
import { CommentsBox } from "@/components/common/CommentsBox";
import { DocumentsBox } from "@/components/common/DocumentsBox";
import { FormDialog } from "@/components/common/FormDialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { taskFields } from "@/lib/schemas";
import { fmtDate, TASK_STATUSES } from "@/lib/format";

export function TaskSheet({ taskId, onClose, onChanged, projectOptions = [] }) {
  const { user } = useAuth();
  const internal = user.role !== "client";
  const { data: t, reload, setData } = useApi(taskId ? `/tasks/${taskId}` : null);
  const { options: staff, name } = useDirectory();
  const [edit, setEdit] = useState(false);
  const [del, setDel] = useState(false);
  const changed = () => { reload(); onChanged?.(); };

  const move = async (status) => {
    try { const r = await api.patch(`/tasks/${taskId}/move`, { status }); setData(r.data); onChanged?.(); toast.success("Estado actualizado"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Sheet open={Boolean(taskId)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="task-sheet">
        {!t || t.id !== taskId ? <Loading /> : (
          <>
            <SheetHeader className="mb-4 text-left">
              <SheetTitle className="font-display text-xl" data-testid="task-sheet-title">{t.title}</SheetTitle>
              <SheetDescription>{[t.client_name, t.project_name].filter(Boolean).join(" · ")}</SheetDescription>
            </SheetHeader>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {internal ? (
                <select className="field-input w-48" value={t.status} onChange={(e) => move(e.target.value)} data-testid="task-sheet-status" aria-label="Estado">
                  {TASK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
              ) : <StatusBadge status={t.status} />}
              <StatusBadge status={t.priority} />
              {t.overdue && <StatusBadge status="Vencida" testid="task-overdue-badge" />}
              {internal && <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid="task-visibility">{t.visible_to_client ? <><Eye className="h-3.5 w-3.5" />Visible para el cliente</> : <><EyeOff className="h-3.5 w-3.5" />Solo interna</>}</span>}
              <div className="flex-1" />
              {internal && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="task-edit-button"><Pencil className="mr-1 h-3.5 w-3.5" />Editar</Button>}
              {user.role === "admin" && <Button size="sm" variant="outline" onClick={() => setDel(true)} data-testid="task-delete-button"><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>}
            </div>
            {t.description && <p className="mb-4 whitespace-pre-wrap text-sm text-slate-700">{t.description}</p>}
            <KV items={[["Responsable", internal ? name(t.assignee_id) : null], ["Inicio", fmtDate(t.start_date)], ["Vence", fmtDate(t.due_date)],
              ...(internal ? [["Tiempo estimado / usado", `${t.estimated_hours ?? "—"} h / ${t.used_hours ?? "—"} h`], ["Recordatorio", fmtDate(t.reminder_date)],
                ["Colaboradores", (t.collaborator_ids || []).map(name).join(", ")], ["Dependencias", (t.dependencies || []).length || null]] : []),
              ["Etiquetas", (t.tags || []).join(", ")]]} />
            <h4 className="mb-2 mt-6 text-sm font-bold">Comentarios</h4>
            <CommentsBox testid="task-comments" comments={t.comments} allowInternal={internal} onAdd={async (text, isInt) => { await api.post(`/tasks/${taskId}/comments`, { text, internal: isInt }); reload(); }} />
            {t.client_id && <><h4 className="mb-2 mt-6 text-sm font-bold">Archivos</h4><DocumentsBox clientId={t.client_id} entityType="tarea" entityId={t.id} testid="task-files" /></>}
            <FormDialog open={edit} onOpenChange={setEdit} title="Editar tarea" testid="task-edit-form" fields={taskFields(staff, projectOptions).filter((f) => f.name !== "project_id")} initial={t}
              onSubmit={async (v) => { await api.patch(`/tasks/${taskId}`, v); toast.success("Tarea actualizada"); changed(); }} />
            <ConfirmDialog open={del} onOpenChange={setDel} destructive title="¿Eliminar tarea?" description="La tarea se ocultará y la acción quedará en el historial." confirmLabel="Eliminar" testid="task-delete-confirm"
              onConfirm={async () => { try { await api.delete(`/tasks/${taskId}`); toast.success("Tarea eliminada"); setDel(false); onChanged?.(); onClose(); } catch (e) { toast.error(errMsg(e)); } }} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
